<template>
  <div class="warehouse-management">
    <div class="page-header">
      <h2>仓库管理</h2>
      <div class="header-actions">
        <a-button type="primary" @click="handleAdd">
          <template #icon>
            <PlusOutlined />
          </template>
          新增仓库
        </a-button>
        <a-button @click="loadWarehouses" :loading="loading">
          <template #icon>
            <ReloadOutlined />
          </template>
          刷新
        </a-button>
      </div>
    </div>

    <a-card>
      <a-table
        :columns="columns"
        :data-source="warehouses"
        :loading="loading"
        :pagination="false"
        row-key="id"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'action'">
            <a-space>
              <a-button type="link" size="small" @click="handleEdit(record)">
                编辑
              </a-button>
              <a-popconfirm
                title="确定要删除这个仓库吗？删除前会检查是否存在库存记录。"
                @confirm="handleDelete(record)"
              >
                <a-button type="link" size="small" danger>
                  删除
                </a-button>
              </a-popconfirm>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>

    <a-modal
      v-model:open="modalVisible"
      :title="modalTitle"
      @ok="handleSubmit"
      @cancel="handleCancel"
      :confirm-loading="submitting"
    >
      <a-form
        ref="formRef"
        :model="formData"
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 16 }"
      >
        <a-form-item
          label="仓库名称"
          name="name"
          :rules="[{ required: true, message: '请输入仓库名称' }]"
        >
          <a-input v-model:value="formData.name" placeholder="请输入仓库名称" />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { message } from 'ant-design-vue'
import {
  PlusOutlined,
  ReloadOutlined
} from '@ant-design/icons-vue'
import { warehouseApi } from '@/api/warehouse'

export default {
  name: 'Warehouse',
  components: {
    PlusOutlined,
    ReloadOutlined
  },
  setup() {
    const loading = ref(false)
    const submitting = ref(false)
    const warehouses = ref([])
    const modalVisible = ref(false)
    const formRef = ref(null)
    const editingId = ref(null)

    const formData = reactive({
      name: ''
    })

    const columns = [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 80
      },
      {
        title: '仓库名称',
        dataIndex: 'name',
        key: 'name'
      },
      {
        title: '创建时间',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 180
      },
      {
        title: '操作',
        key: 'action',
        width: 150
      }
    ]

    const modalTitle = computed(() => {
      return editingId.value ? '编辑仓库' : '新增仓库'
    })

    const loadWarehouses = async () => {
      loading.value = true
      try {
        const sessionId = localStorage.getItem('sessionId')
        const result = await warehouseApi.list({
          sessionId,
          data: {}
        })

        if (result.code === '0') {
          warehouses.value = result.data || []
        } else {
          message.error(result.msg || '加载仓库列表失败')
        }
      } catch (error) {
        console.error('加载仓库列表失败:', error)
        message.error('加载仓库列表失败')
      } finally {
        loading.value = false
      }
    }

    const handleAdd = () => {
      editingId.value = null
      formData.name = ''
      modalVisible.value = true
    }

    const handleEdit = (record) => {
      editingId.value = record.id
      formData.name = record.name
      modalVisible.value = true
    }

    const handleDelete = async (record) => {
      try {
        const sessionId = localStorage.getItem('sessionId')
        const result = await warehouseApi.delete({
          sessionId,
          data: {
            id: record.id
          }
        })

        if (result.code === '0') {
          message.success('删除成功')
          loadWarehouses()
        } else {
          message.error(result.msg || '删除失败')
        }
      } catch (error) {
        console.error('删除失败:', error)
        message.error(error.message || '删除失败')
      }
    }

    const handleSubmit = async () => {
      try {
        await formRef.value.validateFields()

        submitting.value = true
        const sessionId = localStorage.getItem('sessionId')

        let result
        if (editingId.value) {
          result = await warehouseApi.update({
            sessionId,
            data: {
              id: editingId.value,
              name: formData.name
            }
          })
        } else {
          result = await warehouseApi.create({
            sessionId,
            data: {
              name: formData.name
            }
          })
        }

        if (result.code === '0') {
          message.success(editingId.value ? '更新成功' : '创建成功')
          modalVisible.value = false
          loadWarehouses()
        } else {
          message.error(result.msg || '操作失败')
        }
      } catch (error) {
        if (error.errorFields) {
          return
        }
        console.error('操作失败:', error)
        message.error(error.message || '操作失败')
      } finally {
        submitting.value = false
      }
    }

    const handleCancel = () => {
      modalVisible.value = false
      formData.name = ''
      editingId.value = null
    }

    onMounted(() => {
      loadWarehouses()
    })

    return {
      loading,
      submitting,
      warehouses,
      modalVisible,
      modalTitle,
      formRef,
      formData,
      columns,
      loadWarehouses,
      handleAdd,
      handleEdit,
      handleDelete,
      handleSubmit,
      handleCancel
    }
  }
}
</script>

<style scoped>
.warehouse-management {
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 8px;
}
</style>
